import {
  Upload,
  message,
  Space,
  Alert,
  Typography,
  UploadProps,
  Select,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";
import { uploadDocumentToLibrary } from "../../../../lib/api/resources/documentLibraries";
import { fetchCompanies } from "../../../../lib/api/resources/companies";
import { Modal } from "@/components/v2";
import { Button, Card, CardFooter } from "@rumbledirect/ui";

const { Dragger } = Upload;

interface UploadDocumentModalProps {
  libraryId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const UploadDocumentModal = ({
  libraryId,
  open,
  setOpen,
}: UploadDocumentModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);

  const queryClient = useQueryClient();

  const companiesQuery = useQuery({
    queryKey: ["companies"],
    queryFn: () => fetchCompanies({ pagination: false }),
    enabled: open,
    select: (data) =>
      data.member.map((company: Company) => ({
        label: company.fullName,
        value: company.id,
      })),
  });

  const uploadDocumentMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("No file uploaded");
      if (!libraryId) throw new Error("No library ID provided");
      return uploadDocumentToLibrary(libraryId, file, companyId);
    },
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: ["documentLibraryItems"],
      });
      message.success("Document succesvol geüpload");
      setFile(null);
      setCompanyId(undefined);
      setOpen(false);
    },
    onError: () =>
      message.error(
        "Er is een fout opgetreden bij het uploaden van het document",
      ),
  });

  const props: UploadProps = {
    name: "file",
    multiple: false,
    beforeUpload: (f) => {
      setFile(f);
      return false;
    },
    accept: "application/pdf",
    maxCount: 1,
  };

  const handleCancel = () => {
    setFile(null);
    setCompanyId(undefined);
    setOpen(false);
  };

  return (
    <Modal open={open} onCancel={handleCancel}>
      <Card title="Document uploaden">
        {" "}
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Alert type="info" showIcon message="Upload een PDF-bestand." />
          <Dragger {...props} disabled={uploadDocumentMutation.isPending}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <Typography.Paragraph>
              Klik of sleep een PDF-bestand naar dit gebied om te uploaden
            </Typography.Paragraph>
          </Dragger>
          <Select
            allowClear
            showSearch
            style={{ width: "100%" }}
            placeholder="Selecteer een bedrijf (optioneel)"
            options={companiesQuery.data}
            loading={companiesQuery.isLoading}
            value={companyId}
            onChange={(value) => setCompanyId(value)}
            optionFilterProp="label"
            disabled={uploadDocumentMutation.isPending}
          />
        </Space>
        <CardFooter>
          <Button
            type="primary"
            onClick={() => uploadDocumentMutation.mutate()}
            disabled={!file}
            loading={uploadDocumentMutation.isPending}
          >
            Uploaden
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  );
};
